/**
 * Single sign-in across both deployments.
 *
 * Login authenticates against PROD (email + password + TOTP). Prod signs
 * the session token; dev verifies it against prod's public key and mints
 * its own session. One login, both environments, no shared secret.
 */
import { ENVIRONMENTS } from './environments';
import { adminSession, type AdminSession } from './api';

/**
 * Exchange a prod token for a dev session. Non-fatal by design: a
 * federation failure must not block a working prod login — the user
 * lands in prod and the dev toggle reports the problem (toggling to dev
 * without a valid dev session redirects to sign-in there, the same as
 * any other expired/missing session).
 */
export async function federateToDev(prodToken: string): Promise<boolean> {
  try {
    const res = await fetch(`${ENVIRONMENTS.dev.apiBase}/admin/auth/federate`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${prodToken}` },
    });
    if (!res.ok) return false;
    // Shape is issueAdminSession's, shared with /admin/auth/signin —
    // { access, expires_at (ISO string), admin }. Reuse api.ts's
    // AdminSession type rather than redeclaring it, so the two cannot
    // drift.
    const body = (await res.json()) as AdminSession;
    adminSession.set(body.access, body.expires_at, body.admin, 'dev');
    return true;
  } catch {
    return false;
  }
}
